import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";

export interface ScaledIframeRef {
  enterFullscreen: () => void;
}

const ScaledIframe = forwardRef<ScaledIframeRef, { src: string }>(
  ({ src }, ref) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const fullscreenRef = useRef<HTMLDivElement>(null);

    const [scale, setScale] = useState(1);

    useEffect(() => {
      const updateScale = () => {
        if (!wrapperRef.current) return;

        // Measure both the width and height of the wrapper
        const w = wrapperRef.current.offsetWidth;
        const h = wrapperRef.current.offsetHeight;

        // Calculate scale for both dimensions
        const scaleX = w / 1920;
        const scaleY = h / 1080;

        // Use the smaller scale so it never gets cut off (like object-fit: contain)
        setScale(Math.min(scaleX, scaleY));
      };

      updateScale();

      const ro = new ResizeObserver(updateScale);

      if (wrapperRef.current) {
        ro.observe(wrapperRef.current);
      }

      return () => ro.disconnect();
    }, []);

    const enterFullscreen = async () => {
      if (!fullscreenRef.current) return;

      if (fullscreenRef.current.requestFullscreen) {
        await fullscreenRef.current.requestFullscreen();
      }
    };

    useImperativeHandle(ref, () => ({
      enterFullscreen,
    }));

    return (
      <div
        ref={fullscreenRef}
        className="w-full h-full" // Take up full space of the parent
        style={{ zIndex: 10 }}
      >
        <div
          ref={wrapperRef}
          // Flexbox added here to perfectly center the iframe in the leftover space
          className="w-full h-full flex items-center justify-center overflow-hidden bg-black"
        >
          {/* This inner div reserves the exact scaled space so flexbox can center it */}
          <div
            style={{
              width: 1920 * scale,
              height: 1080 * scale,
              position: "relative",
            }}
          >
            <iframe
              src={src}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "1920px",
                height: "1080px",
                border: "none",
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                pointerEvents: "none",
                display: "block",
              }}
              title="TV Theme Preview"
              allow="autoplay"
            />
          </div>
        </div>
      </div>
    );
  },
);

export default ScaledIframe;
